import { useTranslation } from "react-i18next";
import Icon from "../components/Icon";
import Modal from "../components/Modal";
import Reveal from "../components/Reveal";
import SectionHead from "../components/SectionHead";
import { PRODUCTS, getProduct } from "../data/products";
import { useL } from "../lib/useLang";
import { scrollToSection } from "../lib/navigation";

/** Detalle completo de un producto dentro del modal. */
export function ProductDetail({ product, onClose }) {
  const { t } = useTranslation();
  const L = useL();

  return (
    <div>
      <p style={{ fontSize: "var(--fsg-fs-lead)" }}>{L(product.promise)}</p>

      <div className="fsg-card fsg-card--flat" style={{ marginBottom: "var(--fsg-s-5)" }}>
        <h3 style={{ fontSize: "var(--fsg-fs-h4)" }}>{t("products.forWho")}</h3>
        <p style={{ marginBottom: 0 }}>{L(product.forWho)}</p>
      </div>

      <p>{L(product.summary)}</p>

      <h3 style={{ marginTop: "var(--fsg-s-6)" }}>{t("products.covers")}</h3>
      <div className="fsg-coverage-grid">
        {product.coverages.map((coverage) => (
          <div className="fsg-coverage" key={L(coverage.title)}>
            <span className="fsg-coverage__amount">{L(coverage.amount)}</span>
            <h3>{L(coverage.title)}</h3>
            <p>{L(coverage.detail)}</p>
          </div>
        ))}
      </div>

      {product.plans && (
        <>
          <h3 style={{ marginTop: "var(--fsg-s-6)" }}>{t("products.plans")}</h3>
          <div className="fsg-grid fsg-grid--3">
            {product.plans.map((plan) => (
              <div
                className={`fsg-card ${plan.featured ? "fsg-card--flat" : ""}`}
                key={plan.id}
              >
                <h4>{L(plan.name)}</h4>
                <p>{L(plan.detail)}</p>
              </div>
            ))}
          </div>
        </>
      )}

      <h3 style={{ marginTop: "var(--fsg-s-6)" }}>
        {t("products.notCovered")}
      </h3>
      <p className="fsg-hint" style={{ marginBottom: "var(--fsg-s-4)" }}>
        {t("products.notCoveredNote")}
      </p>
      <div className="fsg-alert fsg-alert--warn">
        <Icon name="minus" />
        <ul style={{ display: "grid", gap: ".5rem" }}>
          {L(product.notCovered).map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>

      <h3 style={{ marginTop: "var(--fsg-s-6)" }}>{t("products.ask")}</h3>
      <ul className="fsg-checklist">
        {L(product.ask).map((item) => (
          <li key={item}>
            <Icon name="check-circle" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <div className="fsg-btn-row" style={{ marginTop: "var(--fsg-s-6)" }}>
        <button
          type="button"
          className="fsg-btn fsg-btn--primary"
          onClick={() => {
            onClose();
            scrollToSection("contacto");
          }}
        >
          {t("cta.talk")}
          <Icon name="arrow" />
        </button>
      </div>
    </div>
  );
}

export default function Products({ openId, onOpen, onClose }) {
  const { t } = useTranslation();
  const L = useL();
  const active = openId ? getProduct(openId) : null;

  return (
    <section className="fsg-section" id="productos">
      <div className="fsg-container">
        <SectionHead
          eyebrow={t("products.eyebrow")}
          title={t("products.title")}
          lead={t("products.lead")}
        />

        <div className="fsg-grid fsg-grid--2">
          {PRODUCTS.map((product, index) => (
            <Reveal
              as="article"
              className="fsg-product-card"
              key={product.id}
              delay={index * 70}
            >
              <div className="fsg-product-card__media">
                <img
                  src={product.image}
                  alt=""
                  loading="lazy"
                  width={640}
                  height={400}
                />
                <span className="fsg-badge">{L(product.tag)}</span>
              </div>

              <div className="fsg-product-card__body">
                <h3>{L(product.name)}</h3>
                <p>{L(product.promise)}</p>

                <ul className="fsg-product-card__points">
                  {product.coverages.slice(0, 3).map((coverage) => (
                    <li key={L(coverage.title)}>
                      <Icon name="check-circle" />
                      <span>
                        <strong>{L(coverage.amount)}</strong>{" "}
                        {L(coverage.title).toLowerCase()}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="fsg-card__foot">
                  <button
                    type="button"
                    className="fsg-btn fsg-btn--ghost fsg-btn--block"
                    onClick={() => onOpen(product.id)}
                  >
                    {t("cta.viewDetail")}
                    <Icon name="arrow" />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal
          className="fsg-card fsg-card--flat"
          style={{
            marginTop: "var(--fsg-s-6)",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "var(--fsg-s-4)",
          }}
        >
          <div>
            <h3 style={{ fontSize: "var(--fsg-fs-h4)", marginBottom: ".2rem" }}>
              {t("products.unsure")}
            </h3>
            <p style={{ marginBottom: 0 }}>{t("products.unsureText")}</p>
          </div>
          <button
            type="button"
            className="fsg-btn fsg-btn--primary"
            onClick={() => scrollToSection("herramientas")}
          >
            <Icon name="sparkles" />
            {t("recommender.title")}
          </button>
        </Reveal>
      </div>

      <Modal
        open={Boolean(active)}
        onClose={onClose}
        size="lg"
        title={active ? L(active.name) : ""}
      >
        {active && <ProductDetail product={active} onClose={onClose} />}
      </Modal>
    </section>
  );
}
